import { useQuery } from '@tanstack/react-query';
import type { Database } from '@pirouette/db';
import { supabase } from '@/lib/supabase';

// Owner BI data layer.
//
// Mirrors @/data/reports.ts and @/data/costumes.ts: thin, typed react-query
// wrappers over `supabase.rpc(...)`. Every row type is derived from the
// generated `@pirouette/db` Database types so the UI tracks the database
// contract. Nothing here bypasses RLS — each call runs as the authenticated
// admin and the per-table policies scope it to the caller's studio (tenant).

type RpcReturns<K extends keyof Database['public']['Functions']> =
  Database['public']['Functions'][K]['Returns'];

// ---------------------------------------------------------------------------
// Owner snapshot — bi_owner_snapshot() returns a single `jsonb` object, so the
// generated type is `Json`. We model the contract explicitly here and narrow
// the payload at the read boundary (same approach costumes.ts uses for the
// `Json`-typed recommend_size_preview RPC). Treat every field as possibly
// absent — it is computed server-side from whatever data exists.
// ---------------------------------------------------------------------------

/** One item in the "Do next" action queue from the owner snapshot. */
export interface OwnerSnapshotAction {
  kind: string;
  label: string;
  count: number | null;
  amount_cents: number | null;
  /** App-internal path the action routes to (e.g. `/admin/billing`). */
  link: string;
}

export interface OwnerSnapshot {
  collected_mtd_cents: number;
  billed_mtd_cents: number;
  ar_total_cents: number;
  ar_over_60_cents: number;
  failing_payments: number;
  low_fill_classes: number;
  unpaid_costume_cents: number;
  actions: OwnerSnapshotAction[];
}

/** AR aging row, keyed per family with the standard 30/60/90 buckets. */
export type ArAgingRow = RpcReturns<'bi_ar_aging'>[number];

/** Capacity fill row: enrolled vs capacity (plus waitlist) for one class. */
export type CapacityFillRow = RpcReturns<'bi_capacity_fill'>[number];

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Owner snapshot: MTD money, AR totals, exception counts, and an action queue. */
export function useOwnerSnapshot() {
  return useQuery({
    queryKey: ['bi', 'owner-snapshot'],
    queryFn: async (): Promise<OwnerSnapshot> => {
      const { data, error } = await supabase.rpc('bi_owner_snapshot');
      if (error) throw error;
      return narrowSnapshot(data);
    },
  });
}

/** AR aging by family (current / 1-30 / 31-60 / 61-90 / 90+). */
export function useArAging() {
  return useQuery({
    queryKey: ['bi', 'ar-aging'],
    queryFn: async (): Promise<ArAgingRow[]> => {
      const { data, error } = await supabase.rpc('bi_ar_aging');
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Capacity fill per class (enrolled, waitlisted, fill rate). */
export function useCapacityFill() {
  return useQuery({
    queryKey: ['bi', 'capacity-fill'],
    queryFn: async (): Promise<CapacityFillRow[]> => {
      const { data, error } = await supabase.rpc('bi_capacity_fill');
      if (error) throw error;
      return data ?? [];
    },
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asNullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

/** Coerce one action-queue entry from the `Json` payload into a typed action. */
function narrowAction(value: unknown): OwnerSnapshotAction {
  const obj = (value ?? {}) as Record<string, unknown>;
  return {
    kind: asString(obj.kind),
    label: asString(obj.label),
    count: asNullableNumber(obj.count),
    amount_cents: asNullableNumber(obj.amount_cents),
    link: asString(obj.link),
  };
}

/** Narrow the `Json` bi_owner_snapshot payload into a typed {@link OwnerSnapshot}. */
function narrowSnapshot(value: unknown): OwnerSnapshot {
  const obj = (value ?? {}) as Record<string, unknown>;
  const actions = Array.isArray(obj.actions) ? obj.actions.map(narrowAction) : [];
  return {
    collected_mtd_cents: asNumber(obj.collected_mtd_cents),
    billed_mtd_cents: asNumber(obj.billed_mtd_cents),
    ar_total_cents: asNumber(obj.ar_total_cents),
    ar_over_60_cents: asNumber(obj.ar_over_60_cents),
    failing_payments: asNumber(obj.failing_payments),
    low_fill_classes: asNumber(obj.low_fill_classes),
    unpaid_costume_cents: asNumber(obj.unpaid_costume_cents),
    actions,
  };
}
